import React, { useState } from "react";
import Checkbox, { CheckBoxSize } from "src/components/Common/ui/Checkbox";
import { FolderType, ShareFolderTypes } from "src/Core/Common/Enums";
import { MessageToolbarConstants } from "src/Core/Common/Constants";
import { useSelector } from "react-redux";
import { AppNotifier } from "src/components/Common/Notification/AppNotifier";
import { ApplicationState } from "src/store";
import { IResourceLocator } from "../../../helper/ResourceIdLocators";
import { CancelTransferIcon } from "src/assets/images/svg/IconCollection";
import { IFolderModel } from "src/Core/ViewModels/Folder/FolderModel";
import { isFolderOwner } from "src/helper/HelperFunctions";
import { DeleteIcon, MarkAsNotSpamIcon, MoveToIcon, ResendAccessLinkIcon } from "src/assets/images/svg/IconCollection";
import { ToasterMessages } from "src/helper/ToasterMessages";
import { CustomModal } from "src/components/Common/Modal/CustomModal";

interface IMessageToolbarProps {
    folderId?: number;
    folderType?: FolderType;
    isAllSelected?: boolean;
    isAnySelected?: boolean;
    resourceLocators: IResourceLocator;
    isCancelTransferAllowed?: boolean;
    isMoveToFoldersAllowed?: boolean;
    isResendAccessAllowed?: boolean;
    changeExpiry?: (expiresDate: Date, callback?: any) => void;
    deleteMessages?: (callback?: any) => void;
    selectAll?: (e: any) => void;
    markAsNotSpam?: (callback?: any) => void;
    moveToInbox?: (callback?: any) => void;
    printDocument?: () => void;
    cancelTransfer?: (callback?: any) => void;
    getFolders?: () => any;
    checkFolderExpiry?: (folderId: number) => boolean;
    moveToFolder?: (folderId: number, callback?: any) => void;
    openMoveToFolder?: () => void;
    resendAccessLink?: () => void;
    resendQAAuthAnswer?: () => void;
    reloadMessages?: () => void;
    showSelectAllCheckbox?: boolean;
    hideMessageDetailView?: () => void;
    resetSelection?: () => void;
    folder?: IFolderModel;
    resetSearchAndSort?: () => void; 
}

const MessageToolbar: React.FC<IMessageToolbarProps> = props => {
    const { messageHistorySettings } = useSelector((state: ApplicationState) => state.adminSettingsState);
    const [showDelete, toggleShowDelete] = useState(false);
    const [showSentMessageDelete, toggleShowSentMessageDelete] = useState(false);
    const [showCancelTransfer, toggleShowCancelTransfer] = useState(false);
    const { resourceLocators } = props;
    const userOwnedFolders = useSelector((appState: ApplicationState) => appState.folderState.userOwnedFolders);
    const { mailboxUserData } = useSelector((state: ApplicationState) => state.commonState);
    const user = useSelector((state: ApplicationState) => state.userProfile);
    const handleOnHideDelete = () => {
        toggleShowDelete(false);
    };
    let isMoveToFolderEnabled =
        userOwnedFolders &&
        userOwnedFolders.some(folder => {
            return isFolderOwner(folder, user, mailboxUserData);
        });

    const handleOnSubmitDelete = () => {
        props.deleteMessages(result => {
            if (result) {
                AppNotifier.Success(ToasterMessages.SUCCESS.DELETE_MESSAGES);
                toggleShowDelete(false);
                toggleShowSentMessageDelete(false);
                props.reloadMessages && props.reloadMessages();
                props.hideMessageDetailView && props.hideMessageDetailView();
                props.resetSearchAndSort && props.resetSearchAndSort();
            }
        });
    };

    const handleOnClickDelete = (e: any) => {
        e.stopPropagation();
        toggleShowDelete(true);
    };

    const handleOnClickMarkAsNotSpam = e => {
        e.stopPropagation();
        if (props.markAsNotSpam) {
            props.markAsNotSpam(result => {
                if (result) {
                    AppNotifier.Success(ToasterMessages.SUCCESS.MARK_AS_NOT_SPAM_MESSAGE);
                    props.reloadMessages && props.reloadMessages();
                    props.hideMessageDetailView && props.hideMessageDetailView();
                    props.resetSearchAndSort && props.resetSearchAndSort();
                }
            });
        }
    };

    const handleOnClickMoveToInbox = e => {
        e.stopPropagation();
        if (props.moveToInbox) {
            props.moveToInbox(result => {
                if (result) {
                    AppNotifier.Success(ToasterMessages.SUCCESS.MOVE_TO_INBOX);
                    props.reloadMessages && props.reloadMessages();
                    props.hideMessageDetailView && props.hideMessageDetailView();
                    props.resetSearchAndSort && props.resetSearchAndSort();
                }
            });
        }
    };

    const handleOnHideSentMessageDelete = () => {
        toggleShowSentMessageDelete(false);
    };

    const HandleOnHideCancelTransfer = () => {
        toggleShowCancelTransfer(false);
    };

    const handleOnCancelTransfer = () => {
        if (props.cancelTransfer) {
            props.cancelTransfer((result: boolean) => {
                AppNotifier.Success(ToasterMessages.SUCCESS.CANCEL_MESSAGES);
                toggleShowCancelTransfer(false);
                result && props.reloadMessages && props.reloadMessages();
                props.hideMessageDetailView && props.hideMessageDetailView();
                props.resetSearchAndSort && props.resetSearchAndSort();
            });
        }
    };

    const handleOnClickCancelTransfer = (e: any) => {
        e.stopPropagation();
        toggleShowCancelTransfer(true);
    };

    const handleOnClickSentMessageDelete = e => {
        e.stopPropagation();
        if (mailboxUserData) {
            toggleShowDelete(true);
        } else {
            toggleShowSentMessageDelete(true);
        }
    };

    const handleOnClickMoveToFolder = e => {
        e.stopPropagation();
        props.openMoveToFolder();
    };

    const handleResendAccessLink = e => {
        e.stopPropagation();
        if (props.resendAccessLink) {
            props.resendAccessLink();
        }
    };
    const enableEditPermission = (folder?: IFolderModel) => {
        if (
            !folder ||
            isFolderOwner(folder, user, mailboxUserData) ||
            folder.permissionGroupId == ShareFolderTypes.EDIT
        ) {
            return true;
        } else if (folder.permissionGroupId == ShareFolderTypes.VIEW) {
            return false;
        }
    };

    const isMoveEnabled = (folder?: IFolderModel) => {
        if (!folder || isFolderOwner(folder, user, mailboxUserData)) {
            return true;
        }
        return false;
    };

    return (
        <>
            {(isMoveToFolderEnabled = isMoveToFolderEnabled && isMoveEnabled(props.folder))}
            <div className="message-toolbar">
                <div
                    style={{
                        visibility: props.showSelectAllCheckbox ? "inherit" : "hidden",
                        paddingTop: "2px"
                    }}
                    className="message-toolbar__action"
                >
                    <Checkbox
                        id="mpiCheckbox"
                        text=""
                        size={CheckBoxSize.sm}
                        className="message-toolbar__checkbox"
                        checked={props.isAllSelected || props.isAnySelected}
                        onChange={props.selectAll}
                        indeterminate={props.isAllSelected ? false : props.isAnySelected}
                        disabled={!enableEditPermission(props.folder)}
                        onClick={e => {
                            if (!enableEditPermission(props.folder)) {
                                e.stopPropagation();
                                e.preventDefault();
                            }
                        }}
                    />
                </div>
                {props.folderType !== FolderType.SENT ? (
                    <div className="message-toolbar__action">
                        <span
                            onClick={e => props.isAnySelected && handleOnClickDelete(e)}
                            data-resource-id={resourceLocators.MsgListBtnDelete}
                            title={MessageToolbarConstants.TITLE_DELETE}
                            className={!props.isAnySelected ? "toolbar-icon-disabled" : ""}
                        >
                            <DeleteIcon />
                        </span>
                    </div>
                ) : (
                    <>
                        <div className="message-toolbar__action">
                            <span
                                onClick={e => props.isAnySelected && handleOnClickSentMessageDelete(e)}
                                data-resource-id={resourceLocators.MsgListBtnDelete}
                                title={MessageToolbarConstants.TITLE_DELETE}
                                className={!props.isAnySelected ? "toolbar-icon-disabled" : ""}
                            >
                                <DeleteIcon />
                            </span>
                        </div>
                        <div className="message-toolbar__action">
                            <span
                                onClick={e => props.isAnySelected && props.isCancelTransferAllowed && handleOnClickCancelTransfer(e)}
                                data-resource-id={resourceLocators.MsgListBtnCancelTransfer}
                                title={MessageToolbarConstants.TITLE_CANCEL_TRANSFER}
                                className={!props.isAnySelected || !props.isCancelTransferAllowed ? "toolbar-icon-disabled" : ""}
                            >
                                <CancelTransferIcon
                                  
                                    disabled={!props.isAnySelected || !props.isCancelTransferAllowed}
                                />
                            </span>
                        </div>
                    </>
                )}

                {props.folderType !== FolderType.SPAM && props.folderType !== FolderType.SENT && (
                    <div className="message-toolbar__action">
                        <span
                            onClick={e => props.isAnySelected && isMoveToFolderEnabled && props.isMoveToFoldersAllowed && handleOnClickMoveToFolder(e)}
                            data-resource-id={resourceLocators.MsgListBtnMoveMessage}
                            title={MessageToolbarConstants.TITLE_MOVE_TO_FOLDER}
                            className={!props.isAnySelected || !isMoveToFolderEnabled || !props.isMoveToFoldersAllowed ? "toolbar-icon-disabled" : ""}
                        >
                            <MoveToIcon
                                width={17}
                                height={17}
                            />
                        </span>
                    </div>
                )}
                {!mailboxUserData && props.folderType === FolderType.SPAM && (
                    <div className="message-toolbar__action">
                        <span
                            onClick={e => props.isAnySelected && handleOnClickMarkAsNotSpam(e)}
                            data-resource-id={resourceLocators.MsgListBtnMarkNotSpam}
                            title={MessageToolbarConstants.TITLE_NOT_SPAM}
                            className={!props.isAnySelected ? "toolbar-icon-disabled" : ""}
                        >
                            <MarkAsNotSpamIcon width={16} height={16}/>
                        </span>
                    </div>
                )}
                {!mailboxUserData && props.folderType === FolderType.SPAM && (
                    <div className="message-toolbar__action">
                        <span
                            onClick={e => props.isAnySelected && handleOnClickMoveToInbox(e)}
                            title={MessageToolbarConstants.TITLE_MOVE_TO_INBOX}
                            data-resource-id={resourceLocators.MsgListBtnMoveToInbox}
                            className={!props.isAnySelected ? "toolbar-icon-disabled" : ""}
                        >
                            <MoveToIcon width={17} height={17}/>
                        </span>
                    </div>
                )}

                {!mailboxUserData &&
                    props.folderType === FolderType.SENT &&
                    messageHistorySettings.sentItemsSettings?.isUserAllowedToResendDocumentAccessLinks && (
                        <div className="message-toolbar__action resend-access-icon-wrapper">
                            <span
                                onClick={e => props.isAnySelected && props.isResendAccessAllowed && handleResendAccessLink(e)}
                                data-resource-id={resourceLocators.MsgListBtnResendAccessLink}
                                title={MessageToolbarConstants.TITLE_RESEND_ACCESS_LINK}
                                className={!props.isAnySelected || !props.isResendAccessAllowed ? "toolbar-icon-disabled" : ""}
                            >
                                <ResendAccessLinkIcon/>
                            </span>
                        </div>
                    )}
            </div>
            <CustomModal
                show={showDelete}
                title={MessageToolbarConstants.TITLE_DELETE_POPUP}
                cancelButtonName={MessageToolbarConstants.TITLE_CANCEL}
                confirmButtonName={MessageToolbarConstants.TITLE_DELETE}
                onSubmit={handleOnSubmitDelete}
                onHide={handleOnHideDelete}
                className="delete-message-modal"
                isConfirmModal={true}
            >
                <span>{MessageToolbarConstants.DELETE_MESSAGE}</span>
            </CustomModal>

            <CustomModal
                show={showSentMessageDelete}
                title={MessageToolbarConstants.TITLE_DELETE_POPUP}
                cancelButtonName={MessageToolbarConstants.TITLE_CANCEL}
                confirmButtonName={MessageToolbarConstants.TITLE_DELETE}
                onSubmit={handleOnSubmitDelete}
                onHide={handleOnHideSentMessageDelete}
                isConfirmModal={true}
            >
                <span>{MessageToolbarConstants.DELETE_MESSAGE}</span>
            </CustomModal>

            <CustomModal
                show={showCancelTransfer}
                title={MessageToolbarConstants.TITLE_CANCEL_TRANSFER}
                cancelButtonName={MessageToolbarConstants.TITLE_CANCEL}
                confirmButtonName={MessageToolbarConstants.TITLE_CANCEL_TRANSFER}
                onSubmit={handleOnCancelTransfer}
                onHide={HandleOnHideCancelTransfer}
                isConfirmModal={true}
            >
                <span>{MessageToolbarConstants.CANCEL_TRANSFER_MESSAGE}</span>
            </CustomModal>
        </>
    );
};

export default MessageToolbar;
